import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { fetchTestimonials, submitTestimonial } from "../lib/api";


function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map((n)=>(
        <button key={n} type="button" onMouseEnter={()=>setHover(n)} onMouseLeave={()=>setHover(0)} onClick={()=>onChange(n)} style={{background:"none",border:"none",cursor:"pointer",padding:"2px"}} aria-label={`${n} stars`}>
          <Star size={22} fill={(hover||value)>=n?"#FFD700":"none"} stroke={(hover||value)>=n?"#FFD700":"var(--mt-text-muted)"}/>
        </button>
      ))}
    </div>
  );
}

function StarDisplay({ value, size=14 }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map((n)=>(
        <Star key={n} size={size} fill={value>=n?"#FFD700":"none"} stroke={value>=n?"#FFD700":"#555"}/>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({name:"",rating:0,body:""});
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const ref = useRef(null);

  useEffect(()=>{
    fetchTestimonials().then(setItems).catch(()=>{});
  },[]);

  const all = items.map((t) => ({
    id: t.id,
    author: t.author_name,
    body: t.body,
    rating: 5,
    date: t.created_at ? new Date(t.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "",
  }));
  const scroll=(dir)=>{ const el=ref.current; if(!el)return; el.scrollBy({left:dir*(el.clientWidth*0.8),behavior:"smooth"}); };

  const submit = async (e) => {
    e.preventDefault();
    if(!form.name.trim()||form.rating===0||!form.body.trim())return;
    const res = await submitTestimonial({ authorName: form.name.trim(), body: form.body.trim() });
    setForm({name:"",rating:0,body:""});
    if (res?.ok) {
      setSubmitted(true); setShowForm(false);
      setTimeout(()=>setSubmitted(false),4000);
    }
  };

  return (
    <section className="mt-section relative" data-testid="testimonials-section">
      <div className="mt-container relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 mt-enter">
          <div>
            <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{color:"var(--mt-text-muted)"}}>Worn & Heard</p>
            <h2 className="mt-3 font-display font-extralight leading-[1.08] text-[clamp(2rem,5vw,4rem)]">
              The <span className="italic mt-chrome-text mt-keep-anim">Thread Log.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {all.length>0&&(<>
              <button type="button" onClick={()=>scroll(-1)} className="mt-glass rounded-full p-2.5 hidden md:flex" aria-label="Previous"><ChevronLeft size={16}/></button>
              <button type="button" onClick={()=>scroll(1)} className="mt-glass rounded-full p-2.5 hidden md:flex" aria-label="Next"><ChevronRight size={16}/></button>
            </>)}
            <button type="button" onClick={()=>setShowForm(!showForm)} className="mt-btn mt-glass" style={{fontSize:"0.75rem",letterSpacing:"0.08em"}}>
              {showForm?"Cancel":"✍ Log Your Wear"}
            </button>
          </div>
        </div>

        {showForm&&(
          <div className="mt-glass mt-rainbow-edge rounded-[22px] p-6 sm:p-8 mb-10 max-w-xl">
            <h3 className="font-display text-xl font-light mb-6">Log Your Wear</h3>
            <form onSubmit={submit}>
              <div className="mb-4">
                <label className="font-mono text-[0.62rem] tracking-mt uppercase block mb-2" style={{color:"var(--mt-text-muted)"}}>Your Name</label>
                <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="e.g. Arjun M." required className="mt-input"/>
              </div>
              <div className="mb-4">
                <label className="font-mono text-[0.62rem] tracking-mt uppercase block mb-2" style={{color:"var(--mt-text-muted)"}}>Rating</label>
                <StarPicker value={form.rating} onChange={(r)=>setForm({...form,rating:r})}/>
              </div>
              <div className="mb-6">
                <label className="font-mono text-[0.62rem] tracking-mt uppercase block mb-2" style={{color:"var(--mt-text-muted)"}}>Your Review</label>
                <textarea value={form.body} onChange={(e)=>setForm({...form,body:e.target.value})} placeholder="Tell us about the fit, quality, feel..." required rows={4} className="mt-input resize-none"/>
              </div>
              <button type="submit" className="mt-btn mt-glass mt-rainbow-edge" style={{letterSpacing:"0.08em"}}>Submit Entry →</button>
            </form>
          </div>
        )}

        {submitted&&(
          <div className="mb-8 rounded-xl px-5 py-3 text-sm font-mono" style={{background:"rgba(0,170,255,0.1)",border:"1px solid rgba(0,170,255,0.3)",color:"#00AAFF"}}>
            ✦ Received. Your entry will appear in the Thread Log once approved.
          </div>
        )}

        {all.length===0 ? (
          <div className="mt-glass rounded-[22px] p-8 sm:p-12 text-center" style={{maxWidth:520}}>
            <p className="font-display text-2xl font-light mb-3">Be the first entry.</p>
            <p className="font-body text-sm" style={{color:"var(--mt-text-muted)"}}>The Thread Log is empty. Wear it. Then write about it.</p>
            <button type="button" onClick={()=>setShowForm(true)} className="mt-btn mt-glass mt-rainbow-edge mt-6" style={{letterSpacing:"0.08em"}}>✍ Log Your Wear</button>
          </div>
        ) : (
          <div ref={ref} className="mt-hscroll" data-testid="testimonials-scroll">
            {all.map((t,i)=>(
              <article key={t.id||i} className="mt-glass mt-rainbow-edge rounded-[22px] p-6 sm:p-8 w-[min(460px,82vw)]">
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full font-display text-lg flex-shrink-0" style={{background:"radial-gradient(circle at 35% 30%, #fff, rgba(192,160,255,0.4))",color:"#0a0a0a",border:"1px solid var(--mt-glass-border)"}}>
                    {(t.author)?.[0]||"?"}
                  </span>
                  <div>
                    <p className="font-display text-base">{t.author}</p>
                    <StarDisplay value={t.rating}/>
                    {t.date&&<p className="font-mono text-[0.55rem] tracking-mt uppercase mt-1" style={{color:"var(--mt-text-muted)"}}>{t.date}</p>}
                  </div>
                </div>
                <p className="mt-5 mt-italic-silver leading-relaxed">&ldquo;{t.body}&rdquo;</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
